import { ChangeDetectorRef, Component, computed, DestroyRef, EventEmitter, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs';
import { RegConfig, TabOption, TabOptionsEnum } from '../../shared/models';
import { AppState } from '../../store';
import * as Selectors from '../../store/selectors/com.selectors';

@Component({
  selector: 'dr-customer-offers-ui-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss']
})
export class TabsComponent implements OnInit {
  options: WritableSignal<TabOption[]> = signal([]);
  readonly visibleOptions = computed(() => {
    return this.options().filter((opt) => opt.show);
  });

  @Output() tabChange = new EventEmitter<TabOption>();

  protected selectedOption: TabOption | undefined = undefined;
  private currentRegViewConfigSignal = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });

  constructor(
    private router: Router,
    private translate: TranslateService,
    private destroy: DestroyRef,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.store
      .select(Selectors.selectCurrentRegViewConfig)
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((r): r is RegConfig => r !== null),
        distinctUntilChanged((prev, curr) => prev === curr),
        debounceTime(50),
        tap((r: RegConfig) => {
          this.setupTabs(r);
        })
      )
      .subscribe();
  }

  private setupTabs(r: RegConfig): void {
    const currentUrl: string = this.router.url;
    const isEditAllowed: boolean = r.gateRules?.post_gate_edits?.allow_offer_edit ?? true;
    const openTabDescription: string = r.graph_view
      ? this.translate.instant('COM.TABS.OPEN.CHART_DESCRIPTION')
      : this.translate.instant('COM.TABS.OPEN.DESCRIPTION');

    this.options.set([
      {
        show: true,
        route: 'open',
        name: this.translate.instant('COM.TABS.OPEN.TAB_TITLE'),
        value: TabOptionsEnum.Open,
        selected: currentUrl.includes('open') || currentUrl === '/',
        descriptionTitle: r.graph_view ? this.translate.instant('COM.TABS.OPEN.CHART_TITLE') : this.translate.instant('COM.TABS.OPEN.TITLE'),
        descriptionText: isEditAllowed ? openTabDescription + r.minimumValue + this.translate.instant('COM.TABS.LABELS.AND') + r.maximumValue : '',
        showEditButton: true,
        bulkFunctionality: true,
        exportFunctionality: true,
        optOut: true
      },
      {
        show: r.tab_display.display_da_tab ?? false,
        route: 'da',
        name: this.translate.instant('COM.TABS.DECLARED_AVAILABILITY.TAB_TITLE'),
        value: TabOptionsEnum.Declared_Availability,
        selected: currentUrl.includes('da'),
        descriptionTitle: this.translate.instant('COM.TABS.DECLARED_AVAILABILITY.TITLE'),
        descriptionText:
          this.translate.instant('COM.TABS.DECLARED_AVAILABILITY.DESCRIPTION') +
          r.minimumValue +
          this.translate.instant('COM.TABS.LABELS.AND') +
          r.maximumValue,
        showEditButton: true,
        bulkFunctionality: true,
        exportFunctionality: false,
        optOut: false
      },
      {
        show: r.tab_display.display_cleared_tab,
        route: 'cleared',
        name: this.translate.instant('COM.TABS.CLEARED.TAB_TITLE'),
        value: TabOptionsEnum.Cleared,
        selected: currentUrl.includes('clear'),
        descriptionTitle: this.translate.instant('COM.TABS.CLEARED.TITLE'),
        descriptionText: '',
        showEditButton: false,
        bulkFunctionality: false,
        exportFunctionality: true,
        optOut: false
      },
      {
        show: r.tab_display.display_schedule_tab,
        route: 'schedule',
        name: this.translate.instant('COM.TABS.SCHEDULE.TAB_TITLE'),
        value: TabOptionsEnum.Schedule,
        selected: currentUrl.includes('schedule'),
        descriptionTitle: this.translate.instant('COM.TABS.SCHEDULE.TITLE'),
        descriptionText:
          this.translate.instant('COM.TABS.SCHEDULE.DESCRIPTION') + r.minimumValue + this.translate.instant('COM.TABS.LABELS.AND') + r.maximumValue,
        showEditButton: true,
        bulkFunctionality: true,
        exportFunctionality: false,
        optOut: true
      },
      {
        show: r.tab_display.display_baselines_tab,
        route: 'baselines',
        name: this.translate.instant('COM.TABS.BASELINES.TAB_TITLE'),
        value: TabOptionsEnum.Baselines,
        selected: currentUrl.includes('baselines'),
        descriptionTitle: this.translate.instant('COM.TABS.BASELINES.TITLE'),
        descriptionText: '',
        showEditButton: true,
        bulkFunctionality: true,
        exportFunctionality: false,
        optOut: false
      }
    ]);

    this.selectedOption = this.findSelectedOption(this.options());
    this.tabChange.emit(this.selectedOption);
    this.cdr.detectChanges();

    this.handleRegConfigChange(r, currentUrl);
  }

  private handleRegConfigChange(r: RegConfig, currentUrl: string): void {
    // Check if user is on a tab that's no longer available and navigate to open tab
    if (currentUrl.includes('/baselines') && !r.tab_display.display_baselines_tab) {
      this.navigateToOpenOrClearedTab('open', r);
      return;
    }

    if (currentUrl.includes('/da') && !r.tab_display.display_da_tab) {
      this.navigateToOpenOrClearedTab('open', r);
      return;
    }

    if (currentUrl.includes('/cleared') && !r.tab_display.display_cleared_tab) {
      this.navigateToOpenOrClearedTab('open', r);
      return;
    }

    if (currentUrl.includes('/schedule') && !r.tab_display.display_schedule_tab) {
      this.navigateToOpenOrClearedTab('open', r);
      return;
    }

    // If staying on the same route (like baselines), force reload so the component picks up any config changes
    if (currentUrl.includes('/baselines') && r.tab_display.display_baselines_tab) {
      this.forceRouteReload('/baselines');
      return;
    }

    if (currentUrl.includes('/da') && r.tab_display.display_da_tab) {
      this.forceRouteReload('/da');
      return;
    }

    if (currentUrl.includes('/schedule') && r.tab_display.display_schedule_tab) {
      this.forceRouteReload('/schedule');
      return;
    }

    // Handle open/cleared navigation with view type change
    if (currentUrl.includes('/open') || currentUrl === '/') {
      this.navigateToOpenOrClearedTab('open', r);
    } else if (currentUrl.includes('/cleared')) {
      this.navigateToOpenOrClearedTab('cleared', r);
    }
  }

  private forceRouteReload(route: string): void {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([route]);
    });
  }

  private navigateToOpenOrClearedTab(tabName: 'open' | 'cleared', regViewConfig?: RegConfig): void {
    const config = regViewConfig ?? this.currentRegViewConfigSignal();

    const targetRoute = config?.graph_view ? `/${tabName}/chart` : `/${tabName}/table`;
    const currentRoute = this.router.url.split('?')[0];

    if (currentRoute === targetRoute) return;

    this.router
      .navigate([targetRoute], {
        skipLocationChange: false,
        replaceUrl: false
      })
      .then((navigated: boolean) => {
        if (!navigated) this.router.navigateByUrl(targetRoute);
      });
  }

  private findSelectedOption(options: TabOption[]): TabOption {
    return options.find((opt) => opt.selected && opt.show) || options.find((opt) => opt.name === 'Open') || options[0];
  }

  protected onTabChange(e: MatButtonToggleChange): void {
    this.selectedOption = e.value;
    this.tabChange.emit(this.selectedOption);

    if (this.selectedOption?.route === 'open') {
      this.navigateToOpenOrClearedTab('open');
    } else if (this.selectedOption?.route === 'cleared') {
      this.navigateToOpenOrClearedTab('cleared');
    } else {
      this.router
        .navigate([this.selectedOption?.route], {
          skipLocationChange: false,
          replaceUrl: false
        })
        .then((navigated: boolean) => {
          if (!navigated) this.router.navigateByUrl(this.selectedOption!.route);
        });
    }
  }
}
